// @flow

// Libs
import React, { Component } from 'react'
import { Map, TileLayer, ZoomControl, LayersControl, LayerGroup } from 'react-leaflet'
import { Switch, Route } from 'react-router-dom'
import 'leaflet-measure'
import L from 'leaflet'
import _map from 'lodash/map'
import _get from 'lodash/get'
import _filter from 'lodash/filter'

// Components
import HeftyFields from './HeftyFields'
import HeftyGrid from '../containers/HeftyGridContainer'
import HeftyExistingGrid from '../containers/HeftyExistingGridContainer'
import MapSearch from 'fc-map-search'
import { SpatialManager } from 'fc-spatial'
import SoilTestLayers from '../containers/SoilTestLayersContainer'
import SearchMarker from './SearchMarker'

// Styles
import styles from '../styles'
import 'leaflet-measure/dist/leaflet-measure.css'
import 'static/leaflet/leaflet.scss'

// Components
import AssetsPanel from '../containers/AssetsPanelContainer'
import Controls from '../containers/ControlsContainer'
import JobsPanel from '../containers/JobsPanelContainer'
import Drawer from './Drawer'
import { MaskComponent } from 'fc-requests'

// Constants
import { MAP_TYPES } from '../constants'
import { BALTIC_SD_LATLON } from 'hfty-map/src/constants'

// Types
import type { Asset } from 'fc-model-assets/types'

// Local Types
type Props = {
  fieldsToRenderOnMap: Asset,
  farms: Asset,
  fieldsBbox: Array<number>,
  selectedField: ?Asset,
  selectedFieldId: ?number,
  selectedFeatureIndex: ?number,
  hoverFeatureIndex: ?number,
  featureOperation: Object,
  selectedMapType: string,
  setMapRef: Function,
  clearMapRef: Function,
  flyTo: Function,
  setView: Function,
  centerMapDefault: Function,
  centerMapToFarm: Function,
  centerMapToField: Function,
  changeBaseLayer: Function,
  panelToggled: boolean,
  gridData: Object,
  togglePanel: Function,
  mapModule: Object,
}

class HeftyMap extends Component {
  props: Props

  constructor(props: Props) {
    super(props)
    this.state = {
      bounds: props.fieldsBbox.length ? props.fieldsBbox : BALTIC_SD_LATLON,
      latLng: null,
    }

    this.flyTo = this.flyTo.bind(this)
    this.zoomToBounds = this.zoomToBounds.bind(this)
    this.onRef = this.onRef.bind(this)
    this.onBaseLayerChange = this.onBaseLayerChange.bind(this)
    this.setLocalState = this.setLocalState.bind(this)
    this._key = '_DEFAULT'
  }

  componentDidMount() {
    const {
      selectedSeasonId,
      selectedFieldIdNotValidated,
      selectedFarmIdNotValidated,
      centerMapDefault,
      centerMapToFarm,
      centerMapToField,
    } = this.props

    this.props.setMapRef(this.leafletElement)

    if (selectedFieldIdNotValidated) {
      centerMapToField(selectedFieldIdNotValidated)
      return
    }

    if (selectedFarmIdNotValidated) {
      centerMapToFarm({ seasonId: selectedSeasonId, farmId: selectedFarmIdNotValidated })
      return
    }

    centerMapDefault()
  }

  componentWillUnmount() {
    this.props.clearMapRef()
  }

  addControlPlaceholders(map: Object) {
    const corners = map._controlCorners
    const l = 'leaflet-'
    const container = map._controlContainer

    function createCorner(vSide: string, hSide: string) {
      let className = l + vSide + ' ' + l + hSide
      corners[vSide + hSide] = L.DomUtil.create('div', className, container)
    }
    createCorner('withDrawingTools', 'right')
  }

  flyTo(latLng: Array<number | string>) {
    this.setState({ latLng: latLng }, (): * => this.props.flyTo(this.state.latLng))
  }

  zoomToBounds(fieldsBbox: Array<number>) {
    this.leafletElement.fitBounds(fieldsBbox)
  }

  onRef(ref: Object) {
    this.leafletElement = ref && ref.leafletElement
    if (ref) {
      this.addControlPlaceholders(ref.leafletElement)
      const measureControl = new L.Control.Measure({
        position: 'topright',
      })
      measureControl.addTo(ref.leafletElement)
    }
  }

  onBaseLayerChange({ name }: { name: string }) {
    localStorage.setItem('hfty-map-url', name)
    this.props.changeBaseLayer(name)
  }

  setLocalState(argument: { type: string, newClass: mapInstance }) {
    const newBounds = argument.target.getBounds()
    if (newBounds) {
      this.state.bounds = newBounds
      this.state.center = argument.target.center
      this.state.zoom = argument.target.zoom
    }
  }

  isDrawingToolsVisible(): boolean {
    return _get(this.props.featureOperation, 'operation', '') === 'EDIT'
  }

  filedsToRender(): Array<Object> {
    const { fields, featureOperation } = this.props
    return this.isDrawingToolsVisible()
      ? _filter(fields, (field: any): boolean => field.id !== featureOperation.model.id)
      : fields
  }

  render(): React.Element<any> {
    const drawingToolsVisible = this.isDrawingToolsVisible()
    return (
      <div
        data-test="hefty-map"
        style={styles.mapDivContainer}
        className={`hefty-map${drawingToolsVisible ? ' edit-mode' : ''}`}
      >
        <MaskComponent
          visible={this.props.featureOperation && this.props.featureOperation.submitting}
          messages={{ request: { status: 'IN_PROGRESS' }, name: 'map' }}
        />
        <Controls />
        <Map
          onMoveEnd={this.setLocalState}
          key={this._key}
          style={styles.map}
          center={this.state.center}
          zoom={this.state.zoom}
          bounds={this.state.bounds}
          ref={this.onRef}
          zoomControl={false}
          onBaseLayerChange={this.onBaseLayerChange}
          preferCanvas={!this.props.featureOperation}
          worldCopyJump
          editable
        >
          {this.state.latLng ? <SearchMarker latLng={this.state.latLng} /> : null}
          <LayersControl position={'topright'}>
            {_map(MAP_TYPES, (mapType: Object): React.Element<any> => {
              return (
                <LayersControl.BaseLayer
                  key={mapType.name}
                  name={mapType.name}
                  checked={this.props.selectedMapType === mapType.name}
                >
                  <LayerGroup>
                    {_map(mapType.urls, (url: string): React.Element<any> => (
                      <TileLayer key={url} url={url} />
                    ))}
                  </LayerGroup>
                </LayersControl.BaseLayer>
              )
            })}
          </LayersControl>
          <SoilTestLayers />
          <ZoomControl position={'topright'} />
          <SpatialManager
            operation={this.props.featureOperation}
            onChange={this.props.onChangeEditSelectedField}
            onSubmit={this.props.onSubmitEditSelectedField}
            onCancel={this.props.onCancelEditSelectedField}
          />
          <Route
            path="/map/jobs/:jobId?"
            render={(props: Object): React.element<any> => (
              <HeftyExistingGrid {...props} gridData={this.props.gridData} />
            )}
          />
          <HeftyFields
            // Data
            fieldsToRenderOnMap={this.props.fieldsToRenderOnMap}
            farms={this.props.farms}
            selectedFieldId={this.props.selectedFieldId}
            selectedFeatureIndex={this.props.selectedFeatureIndex}
            hoverFeatureIndex={this.props.hoverFeatureIndex} // Actions
            onSelectFeature={this.props.onSelectFeature}
            onToggleHoverFeature={this.props.onToggleHoverFeature}
          />
          <Route
            path="/map/jobs"
            render={(props: Object): React.element<any> => <HeftyGrid {...props} gridData={this.props.gridData} />}
          />
        </Map>
        <Drawer
          togglePanel={this.props.togglePanel}
          panelToggled={this.props.panelToggled}
          location={this.props.location}
          onCancelEditSelectedField={this.props.onCancelEditSelectedField}
          onCancelJob={this.props.onCancelJob}
          jobOperations={this.props.jobOperations}
          featureOperation={this.props.featureOperation}
        >
          <Switch>
            <Route
              path="/map/jobs/:jobId?"
              render={(props: Object): React.element<any> => <JobsPanel {...props} gridData={this.props.gridData} />}
            />
            <Route
              path="/map/search"
              render={(): React.Element<any> => <MapSearch flyTo={this.flyTo} fitMapBounds={this.props.fitMapBounds} />}
            />
            <Route path="/map" component={AssetsPanel} />
          </Switch>
        </Drawer>
      </div>
    )
  }
}

export default HeftyMap
